import { useNode, useEditor, Element } from "@craftjs/core";
import { Text } from "./Text";
import { Button as CmsButton } from "./Button";
import { Image as CmsImage } from "./Image";
import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { Box, Typography, IconButton } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { useLanguage, CmsLang } from "../../context/LanguageContext";
import CloseIcon from "@mui/icons-material/Close";
import { ImageUpload } from "../ui/ImageUpload";
import {
  selectedOutlineSx,
  labelStyle,
  inputStyle,
  addButtonStyle,
  removeButtonStyle,
  itemCardStyle,
} from "../../config/settingsStyles";
import {
  TextField,
  CheckboxField,
  ColorField,
  SliderField,
  SelectField,
  SettingsSection,
  SettingsRow,
} from "../ui/form/SettingsField";
import { useLiffUrl } from "../../context/CMSConfigContext";

export interface NavItem {
  label: string;
  href: string;
}

export interface ExtraButton {
  text: string;
  link: string;
  variant: "filled" | "outline" | "text";
  bgColor?: string;
  hideOnDesktop?: boolean;
  hideOnMobile?: boolean;
}

export interface ExtraIcon {
  iconUrl: string;
  link: string;
  bgColor?: string;
}

// Mobile Sidebar Component - uses portal to render in preview container
interface NavbarMobileSidebarProps {
  backgroundColor: string;
  textColor: string;
  navItems: NavItem[];
  extraButtons: ExtraButton[];
  showCTA: boolean;
  ctaText: string;
  ctaLink: string;
  ctaBackgroundColor?: string;
  activeHref?: string;
  onClose: () => void;
  onLinkClick: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

const NavbarMobileSidebar: React.FC<NavbarMobileSidebarProps> = ({
  backgroundColor,
  textColor,
  navItems,
  extraButtons,
  showCTA,
  ctaText,
  ctaLink,
  ctaBackgroundColor,
  activeHref,
  onClose,
  onLinkClick,
}) => {
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Find the preview container or fall back to body
    const previewContainer = document.querySelector(".preview-container") as HTMLElement;
    setContainer(previewContainer || document.body);
    // Trigger animation after mount
    requestAnimationFrame(() => setIsVisible(true));
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300); // Wait for animation to complete
  };

  if (!container) return null;

  const sidebar = (
    <>
      {/* Backdrop */}
      <Box
        onClick={handleClose}
        sx={{
          position: "absolute",
          inset: 0,
          bgcolor: "rgba(0,0,0,0.5)",
          zIndex: 1100,
          opacity: isVisible ? 1 : 0,
          transition: "opacity 0.3s ease-in-out",
        }}
      />
      {/* Sidebar */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          width: "80%",
          maxWidth: "300px",
          bgcolor: backgroundColor,
          boxShadow: "-4px 0 20px rgba(0,0,0,0.15)",
          zIndex: 1200,
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
          transform: isVisible ? "translateX(0)" : "translateX(100%)",
          transition: "transform 0.3s ease-in-out",
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            <IconButton onClick={handleClose} aria-label="Close menu">
              <CloseIcon sx={{ color: textColor }} />
            </IconButton>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {navItems.map((item, index) => {
              const isActive = activeHref === item.href;
              return (
              <Box
                key={index}
                component="a"
                href={item.href}
                onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                  onLinkClick(e);
                  handleClose();
                }}
                sx={{
                  display: "block",
                  py: 1.5,
                  fontWeight: isActive ? 700 : 500,
                  color: textColor,
                  textDecoration: "none",
                  borderBottom: isActive ? "3px solid" : "1px solid #e5e7eb",
                  borderImage: isActive ? "linear-gradient(90deg, #fda0b8, #f9c26f) 1" : "none",
                  "&:hover": { opacity: 0.7 },
                }}
              >
                {item.label}
              </Box>
              );
            })}

            {/* Extra Buttons in sidebar */}
            {extraButtons.map((btn, index) => (
              <Box
                key={`extra-${index}`}
                component="a"
                href={btn.link}
                onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                  onLinkClick(e);
                  handleClose();
                }}
                sx={{
                  display: "block",
                  py: 1,
                  px: btn.variant === "text" ? 0 : 3,
                  borderRadius: btn.variant === "text" ? 0 : 2,
                  fontWeight: btn.variant === "text" ? 500 : 600,
                  textAlign: "center",
                  textDecoration: "none",
                  ...(btn.variant === "filled" && {
                    color: "#ffffff",
                    backgroundColor: btn.bgColor || textColor,
                  }),
                  ...(btn.variant === "outline" && {
                    color: textColor,
                    border: `1.5px solid ${textColor}`,
                    backgroundColor: "transparent",
                  }),
                  ...(btn.variant === "text" && {
                    color: textColor,
                    textAlign: "left",
                    borderBottom: "1px solid #e5e7eb",
                  }),
                  "&:hover": { opacity: 0.7 },
                }}
              >
                {btn.text}
              </Box>
            ))}

            {/* CTA in sidebar */}
            {showCTA && (
              <Box
                component="a"
                href={ctaLink}
                onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                  onLinkClick(e);
                  handleClose();
                }}
                sx={{
                  display: "block",
                  color: "#ffffff",
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  fontWeight: 600,
                  textAlign: "center",
                  textDecoration: "none",
                  background:
                    ctaBackgroundColor || "linear-gradient(135deg, var(--color-primary, #3b82f6) 0%, var(--color-secondary, #10b981) 100%)",
                  "&:hover": { opacity: 0.9 },
                }}
              >
                {ctaText}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );

  return ReactDOM.createPortal(sidebar, container);
};

export interface NavbarProps {
  logoText?: string;
  logoUrl?: string;
  navItems?: NavItem[];
  extraButtons?: ExtraButton[];
  extraIcons?: ExtraIcon[];
  showCTA?: boolean;
  ctaText?: string;
  ctaLink?: string;
  ctaBackgroundColor?: string;
  ctaHideOnDesktop?: boolean;
  ctaHideOnMobile?: boolean;
  backgroundColor?: string;
  textColor?: string;
  sticky?: boolean;
  maxWidth?: number;
  paddingX?: number;
  paddingTop?: number;
  paddingBottom?: number;
  gap?: number;
  mobileBreakpoint?: number;
  showLangSelector?: boolean;
  languages?: string[];
  navLayout?: "center" | "left" | "right";
  navFontSize?: number;
  navFontFamily?: string;
}

export const Navbar = ({
  logoText = "Clinic",
  logoUrl,
  navItems = [],
  extraButtons = [],
  extraIcons = [],
  showCTA = false,
  ctaText = "Book Now",
  ctaLink = "/contact",
  ctaBackgroundColor,
  ctaHideOnDesktop = false,
  ctaHideOnMobile = false,
  backgroundColor = "#ffffff",
  textColor = "#1f2937",
  sticky = true,
  maxWidth = 1280,
  paddingX = 24,
  paddingTop = 12,
  paddingBottom = 12,
  gap = 16,
  mobileBreakpoint = 768,
  showLangSelector = false,
  languages = ["TH", "EN"],
  navLayout = "right",
  navFontSize = 14,
  navFontFamily,
}: NavbarProps) => {
  const {
    connectors: { connect, drag },
    selected,
    actions: { setProp },
  } = useNode((state) => ({
    selected: state.events.selected,
  }));

  const { enabled: editorEnabled } = useEditor((state) => ({
    enabled: state.options.enabled,
  }));

  const [langOpen, setLangOpen] = useState(false);
  const { currentLang, setCurrentLang } = useLanguage();
  const selectedLang = currentLang === "en" ? "EN" : "TH";
  const setSelectedLang = (lang: string) => setCurrentLang(lang.toLowerCase() as CmsLang);
  const langRef = useRef<HTMLDivElement>(null);

  const liffUrl = useLiffUrl();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const firstAnchor = (navItems || []).find((item) => item.href.startsWith("#"));
  const [activeHref, setActiveHref] = useState<string>(firstAnchor ? firstAnchor.href : "");
  const navRef = useRef<HTMLElement>(null);

  // Detect mobile based on container width
  useEffect(() => {
    const navElement = navRef.current;
    if (!navElement) return;

    const checkWidth = () => {
      let container: HTMLElement | null = navElement;
      while (container && container.parentElement) {
        const style = window.getComputedStyle(container.parentElement);
        if (style.containerType === "inline-size" || style.containerType === "size") {
          container = container.parentElement;
          break;
        }
        container = container.parentElement;
      }
      const width = container?.clientWidth || navElement.clientWidth;
      setIsMobile(width < mobileBreakpoint);
    };

    checkWidth();
    const observer = new ResizeObserver(checkWidth);
    observer.observe(navElement);

    let parent = navElement.parentElement;
    while (parent && parent !== document.body) {
      observer.observe(parent);
      parent = parent.parentElement;
    }

    return () => observer.disconnect();
  }, [mobileBreakpoint]);

  useEffect(() => {
    if (!isMobile) setMobileMenuOpen(false);
  }, [isMobile]);

  // Track which section is scrolled into view
  const skipScrollUpdate = useRef(false);

  useEffect(() => {
    const anchorHrefs = (navItems || []).filter((item) => item.href.startsWith("#")).map((item) => item.href);
    if (anchorHrefs.length === 0) return;

    const sectionIds = anchorHrefs.map((href) => href.slice(1));

    // Find the scroll container (nearest ancestor with overflow scroll/auto that actually scrolls)
    const findScrollRoot = (): HTMLElement | null => {
      const navEl = navRef.current;
      if (!navEl) return null;
      let el: HTMLElement | null = navEl.parentElement;
      while (el) {
        const style = window.getComputedStyle(el);
        const oy = style.overflowY || style.overflow;
        if ((oy === "auto" || oy === "scroll") && el.scrollHeight > el.clientHeight) {
          return el;
        }
        el = el.parentElement;
      }
      return null;
    };

    // Get element's offset relative to a container
    const getOffsetTop = (el: HTMLElement, container: HTMLElement): number => {
      let offset = 0;
      let current: HTMLElement | null = el;
      while (current && current !== container) {
        offset += current.offsetTop;
        current = current.offsetParent as HTMLElement | null;
      }
      return offset;
    };

    let scrollRoot: HTMLElement | null = null;
    let rafId: number | null = null;

    const updateActive = () => {
      if (skipScrollUpdate.current) return;
      if (!scrollRoot) scrollRoot = findScrollRoot();
      if (!scrollRoot) return;

      const navEl = navRef.current;
      if (!navEl) return;
      const navHeight = navEl.offsetHeight + 40; // offset past navbar
      const scrollTop = scrollRoot.scrollTop;

      let active = sectionIds[0];
      for (const id of sectionIds) {
        const el = document.getElementById(id);
        if (!el) continue;
        const elTop = getOffsetTop(el, scrollRoot);
        if (scrollTop + navHeight >= elTop) {
          active = id;
        }
      }
      setActiveHref(`#${active}`);
    };

    const onScroll = () => {
      if (rafId != null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        updateActive();
      });
    };

    // Retry finding scrollRoot a few times (elements may not be rendered yet)
    const initTimer = setTimeout(() => {
      scrollRoot = findScrollRoot();
      if (scrollRoot) {
        scrollRoot.addEventListener("scroll", onScroll, { passive: true });
        updateActive();
      }
    }, 200);

    // Also listen on window for non-nested scroll
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      clearTimeout(initTimer);
      if (rafId != null) cancelAnimationFrame(rafId);
      if (scrollRoot) scrollRoot.removeEventListener("scroll", onScroll);
      window.removeEventListener("scroll", onScroll);
    };
  }, [navItems]);

  // Close lang dropdown on outside click
  useEffect(() => {
    if (!langOpen) return;
    const handler = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [langOpen]);

  const scrollToSection = (sid: string) => {
    requestAnimationFrame(() => {
      // Find navbar's scrollable parent first
      let navScrollParent: HTMLElement | null = navRef.current?.parentElement || null;
      while (navScrollParent) {
        const style = window.getComputedStyle(navScrollParent);
        const oy = style.overflowY || style.overflow;
        if ((oy === "auto" || oy === "scroll") && navScrollParent.scrollHeight > navScrollParent.clientHeight) {
          break;
        }
        navScrollParent = navScrollParent.parentElement;
      }

      // Find the target that shares the same scroll parent (handles duplicate IDs)
      const allTargets = document.querySelectorAll(`[data-section-id="${sid}"]`);
      let targetEl: Element | null = null;
      for (const el of allTargets) {
        if (navScrollParent && navScrollParent.contains(el)) {
          targetEl = el;
          break;
        }
      }
      if (!targetEl) targetEl = allTargets[0] || null;

      if (!targetEl) return;

      if (navScrollParent) {
        const targetRect = targetEl.getBoundingClientRect();
        const parentRect = navScrollParent.getBoundingClientRect();
        navScrollParent.scrollTo({
          top: navScrollParent.scrollTop + targetRect.top - parentRect.top,
          behavior: "smooth",
        });
      } else {
        const targetRect = targetEl.getBoundingClientRect();
        window.scrollTo({
          top: window.scrollY + targetRect.top,
          behavior: "smooth",
        });
      }
    });
  };

  // Handle link clicks - smooth scroll for anchor links, prevent default in editor
  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const href = e.currentTarget.getAttribute("href");
    if (href?.startsWith("#")) {
      e.preventDefault();
      e.stopPropagation();
      skipScrollUpdate.current = true;
      setActiveHref(href);
      scrollToSection(href.slice(1));
      setTimeout(() => { skipScrollUpdate.current = false; }, 1000);
    } else if (editorEnabled) {
      // Only block navigation in editor mode; in preview let it bubble to PreviewModal handler
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <Box
      component="nav"
      ref={(ref: HTMLElement | null) => {
        if (ref) {
          connect(drag(ref));
          (navRef as React.MutableRefObject<HTMLElement | null>).current = ref;
        }
      }}
      sx={{
        width: "100%",
        bgcolor: backgroundColor,
        color: textColor,
        position: sticky ? "sticky" : "relative",
        top: sticky ? 0 : "auto",
        zIndex: 1000,
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        ...(selected && { ...selectedOutlineSx, outlineOffset: "-2px" }),
      }}
    >
      <Box
        sx={{
          maxWidth: maxWidth ? `${maxWidth}px` : "none",
          mx: "auto",
          px: `${paddingX}px`,
          pt: `${paddingTop}px`,
          pb: `${paddingBottom}px`,
          minHeight: "56px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: `${gap}px`,
        }}
      >
        {/* Logo (left) */}
        <Box
          component="a"
          href="/"
          onClick={handleLinkClick}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            textDecoration: "none",
            flexShrink: 0,
            ...(navLayout === "center" ? { flex: 1 } : {}),
          }}
        >
          {logoUrl && (
            <Box component="img" src={logoUrl} alt={logoText} sx={{ height: 40, width: "auto" }} />
          )}
          {logoText && (
            <Element
              id="navbar-logo-text"
              is={Text}
              text={logoText}
              fontSize={20}
              fontWeight="bold"
              tagName="span"
              color={textColor}
            />
          )}
        </Box>

        {/* Desktop: Nav items + Buttons */}
        {!isMobile && (
          <>
          <Box sx={{
            display: "flex",
            alignItems: "center",
            gap: `${gap}px`,
            ...(navLayout === "left" ? {} : navLayout === "center" ? { flex: 1, justifyContent: "center" } : { ml: "auto" }),
          }}>
              {navItems.map((item, index) => {
                const isActive = activeHref === item.href;
                return (
                <Box
                  component="a"
                  href={item.href}
                  key={index}
                  onMouseDown={(e: React.MouseEvent) => {
                    if (editorEnabled) e.stopPropagation();
                    if (!editorEnabled && item.href.startsWith("#")) {
                      skipScrollUpdate.current = true;
                      setActiveHref(item.href);
                      scrollToSection(item.href.slice(1));
                      setTimeout(() => { skipScrollUpdate.current = false; }, 1000);
                    }
                  }}
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    if (editorEnabled) {
                      e.stopPropagation();
                    }
                  }}
                  sx={{
                    position: "relative",
                    py: 1,
                    cursor: "pointer",
                    textDecoration: "none",
                    color: "inherit",
                    "&:hover": { opacity: 0.7 },
                  }}
                >
                  <Element
                    id={`nav-item-${index}`}
                    is={Text}
                    text={item.label}
                    fontSize={navFontSize}
                    fontFamily={navFontFamily}
                    fontWeight={isActive ? "bold" : "medium"}
                    tagName="span"
                    color={textColor}
                    whiteSpace="nowrap"
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: "3px",
                      background: "linear-gradient(90deg, #fda0b8, #f9c26f)",
                      borderRadius: "2px",
                      transform: isActive ? "scaleX(1)" : "scaleX(0)",
                      transformOrigin: "center",
                      transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  />
                </Box>
              );
              })}
          </Box>

          {/* Extra Buttons + CTA (right) */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1.5, flexShrink: 0, flexWrap: "nowrap", ...(navLayout === "center" ? { flex: 1 } : {}) }}>
              {/* Language Dropdown */}
              {showLangSelector && (
                <Box
                  ref={langRef}
                  sx={{ position: "relative", display: "inline-block" }}
                >
                  <Box
                    onClick={() => setLangOpen(!langOpen)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      cursor: "pointer",
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: "0.85rem",
                      fontWeight: 500,
                      color: textColor,
                      userSelect: "none",
                      whiteSpace: "nowrap",
                      "&:hover": { opacity: 0.7 },
                    }}
                  >
                    <Box component="img" src={selectedLang === "TH" ? "/static/images/mehey_landing/flag-th.png" : "/static/images/mehey_landing/flag-en.png"} alt={selectedLang} sx={{ width: 20, height: 20, borderRadius: "50%", objectFit: "cover" }} /> {selectedLang} ▾
                  </Box>
                  {langOpen && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        mt: 0.5,
                        bgcolor: "#ffffff",
                        borderRadius: 1,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        overflow: "hidden",
                        zIndex: 1300,
                        minWidth: 80,
                      }}
                    >
                      {languages.map((lang) => (
                        <Box
                          key={lang}
                          onClick={() => {
                            setSelectedLang(lang);
                            setLangOpen(false);
                          }}
                          sx={{
                            px: 1.5,
                            py: 1,
                            fontSize: "0.85rem",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            bgcolor: lang === selectedLang ? "#f3f4f6" : "transparent",
                            "&:hover": { bgcolor: "#f3f4f6" },
                          }}
                        >
                          <Box component="img" src={lang === "TH" ? "/static/images/mehey_landing/flag-th.png" : "/static/images/mehey_landing/flag-en.png"} alt={lang} sx={{ width: 18, height: 18, borderRadius: "50%", objectFit: "cover" }} /> {lang}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              {/* Extra Icons */}
              {extraIcons.map((icon, index) => (
                <Element
                  key={`icon-${index}`}
                  id={`navbar-icon-${index}`}
                  is={CmsImage}
                  src={icon.iconUrl}
                  alt="icon"
                  width="24px"
                  height="24px"
                  objectFit="contain"
                  link={icon.link}
                  linkTarget="_blank"
                />
              ))}

              {extraButtons.filter((b) => !b.hideOnDesktop).map((btn, index) => (
                <Element
                  key={`extra-${index}`}
                  id={`navbar-btn-${index}`}
                  is={CmsButton}
                  text={btn.text}
                  link={btn.link}
                  variant={btn.variant === "filled" ? "primary" : btn.variant === "outline" ? "outline" : "ghost"}
                  size="sm"
                  backgroundColor={btn.variant === "filled" ? (btn.bgColor || textColor) : "transparent"}
                  textColor={btn.variant === "filled" ? "#ffffff" : textColor}
                  borderRadius={8}
                  borderColor={btn.variant === "outline" ? textColor : undefined}
                  borderWidth={btn.variant === "outline" ? 1 : undefined}
                  fontFamily={navFontFamily}
                />
              ))}

              {/* CTA Button */}
              {showCTA && !ctaHideOnDesktop && (
                <Element
                  id="navbar-cta"
                  is={CmsButton}
                  text={ctaText}
                  link={liffUrl ?? ctaLink}
                  variant="primary"
                  size="sm"
                  fontFamily={navFontFamily}
                  backgroundColor={ctaBackgroundColor || "var(--color-primary, #3b82f6)"}
                  textColor="#ffffff"
                  borderRadius={8}
                />
              )}
          </Box>
          </>
        )}

        {/* Mobile: Hamburger button */}
        {isMobile && (navItems.length > 0 || extraButtons.length > 0 || showCTA) && (
          <IconButton
            onClick={() => setMobileMenuOpen(true)}
            sx={{ color: textColor }}
            aria-label="Open menu"
          >
            <MenuIcon />
          </IconButton>
        )}
      </Box>

      {/* Mobile Sidebar - uses portal to stay within preview container */}
      {isMobile && mobileMenuOpen && (
        <NavbarMobileSidebar
          backgroundColor={backgroundColor}
          textColor={textColor}
          navItems={navItems}
          extraButtons={extraButtons.filter((b) => !b.hideOnMobile)}
          showCTA={showCTA && !ctaHideOnMobile}
          ctaText={ctaText}
          ctaLink={liffUrl ?? ctaLink}
          ctaBackgroundColor={ctaBackgroundColor}
          activeHref={activeHref}
          onClose={() => setMobileMenuOpen(false)}
          onLinkClick={handleLinkClick}
        />
      )}
    </Box>
  );
};

export const NavbarSettings = () => {
  const liffUrl = useLiffUrl();

  const {
    actions: { setProp },
    logoText,
    logoUrl,
    navItems,
    extraButtons,
    extraIcons,
    showCTA,
    ctaText,
    ctaLink,
    ctaBackgroundColor,
    ctaHideOnDesktop,
    ctaHideOnMobile,
    backgroundColor,
    textColor,
    sticky,
    maxWidth,
    paddingX,
    paddingTop,
    paddingBottom,
    gap,
    mobileBreakpoint,
    showLangSelector,
    navLayout,
  } = useNode((node) => ({
    logoText: node.data.props.logoText,
    logoUrl: node.data.props.logoUrl,
    navItems: node.data.props.navItems || [],
    extraButtons: node.data.props.extraButtons || [],
    extraIcons: node.data.props.extraIcons || [],
    showCTA: node.data.props.showCTA,
    ctaText: node.data.props.ctaText,
    ctaLink: node.data.props.ctaLink,
    ctaBackgroundColor: node.data.props.ctaBackgroundColor,
    ctaHideOnDesktop: node.data.props.ctaHideOnDesktop,
    ctaHideOnMobile: node.data.props.ctaHideOnMobile,
    backgroundColor: node.data.props.backgroundColor,
    textColor: node.data.props.textColor,
    sticky: node.data.props.sticky,
    maxWidth: node.data.props.maxWidth,
    paddingX: node.data.props.paddingX,
    paddingTop: node.data.props.paddingTop,
    paddingBottom: node.data.props.paddingBottom,
    gap: node.data.props.gap,
    mobileBreakpoint: node.data.props.mobileBreakpoint,
    showLangSelector: node.data.props.showLangSelector,
    navLayout: node.data.props.navLayout,
  }));

  const updateNavItem = (index: number, field: keyof NavItem, value: string) => {
    setProp((props: NavbarProps) => {
      const newItems = [...(props.navItems || [])];
      newItems[index] = { ...newItems[index], [field]: value };
      props.navItems = newItems;
    });
  };

  const addNavItem = () => {
    setProp((props: NavbarProps) => {
      props.navItems = [...(props.navItems || []), { label: "New Link", href: "/new" }];
    });
  };

  const removeNavItem = (index: number) => {
    setProp((props: NavbarProps) => {
      props.navItems = (props.navItems || []).filter((_, i) => i !== index);
    });
  };

  const moveNavItem = (index: number, direction: "up" | "down") => {
    setProp((props: NavbarProps) => {
      if (!props.navItems) return;
      const newIndex = direction === "up" ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= props.navItems.length) return;
      const newItems = [...props.navItems];
      [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
      props.navItems = newItems;
    });
  };

  const updateExtraButton = (index: number, field: keyof ExtraButton, value: string) => {
    setProp((props: NavbarProps) => {
      const newButtons = [...(props.extraButtons || [])];
      newButtons[index] = { ...newButtons[index], [field]: value } as ExtraButton;
      props.extraButtons = newButtons;
    });
  };

  const addExtraButton = () => {
    setProp((props: NavbarProps) => {
      props.extraButtons = [
        ...(props.extraButtons || []),
        { text: "Button", link: "/link", variant: "outline" as const },
      ];
    });
  };

  const removeExtraButton = (index: number) => {
    setProp((props: NavbarProps) => {
      props.extraButtons = (props.extraButtons || []).filter((_, i) => i !== index);
    });
  };

  const moveExtraButton = (index: number, direction: "up" | "down") => {
    setProp((props: NavbarProps) => {
      if (!props.extraButtons) return;
      const newIndex = direction === "up" ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= props.extraButtons.length) return;
      const newButtons = [...props.extraButtons];
      [newButtons[index], newButtons[newIndex]] = [newButtons[newIndex], newButtons[index]];
      props.extraButtons = newButtons;
    });
  };

  return (
    <SettingsSection>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
        💡 คลิกที่ตัวอักษร/ปุ่มบน canvas เพื่อแก้ไข text, สี, ขนาด
      </Typography>

      <ImageUpload
        value={logoUrl || ""}
        onChange={(url) => setProp((props: NavbarProps) => (props.logoUrl = url || undefined))}
        label="Logo Image (optional)"
      />

      <SelectField
        label="Nav Layout"
        value={navLayout || "right"}
        onChange={(v) => setProp((props: NavbarProps) => (props.navLayout = v as NavbarProps["navLayout"]))}
        options={[
          { value: "right", label: "ขวา (Logo | Nav + Buttons)" },
          { value: "center", label: "กลาง (Logo | Nav | Buttons)" },
          { value: "left", label: "ซ้าย (Logo + Nav | Buttons)" },
        ]}
      />

      <div>
        <label style={labelStyle}>Navigation Items</label>
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {(navItems || []).map((item: NavItem, index: number) => (
            <div key={index} style={itemCardStyle}>
              {/* Header with reorder and delete */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "8px",
                }}
              >
                <div style={{ display: "flex", gap: "4px" }}>
                  <button
                    onClick={() => moveNavItem(index, "up")}
                    disabled={index === 0}
                    style={{
                      ...removeButtonStyle,
                      opacity: index === 0 ? 0.3 : 1,
                      padding: "4px 8px",
                    }}
                  >
                    ↑
                  </button>
                  <button
                    onClick={() => moveNavItem(index, "down")}
                    disabled={index === (navItems || []).length - 1}
                    style={{
                      ...removeButtonStyle,
                      opacity: index === (navItems || []).length - 1 ? 0.3 : 1,
                      padding: "4px 8px",
                    }}
                  >
                    ↓
                  </button>
                </div>
                <button onClick={() => removeNavItem(index)} style={removeButtonStyle}>
                  ×
                </button>
              </div>
              {/* Inputs */}
              <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <span style={{ fontSize: "0.75rem", color: "#6b7280" }}>📝 {item.label} — คลิกที่ canvas เพื่อแก้ text</span>
                <input
                  type="text"
                  value={item.href}
                  onChange={(e) => updateNavItem(index, "href", e.target.value)}
                  style={inputStyle}
                  placeholder="Link (e.g. #hero, /about)"
                />
              </div>
            </div>
          ))}
        </div>
        <button onClick={addNavItem} style={addButtonStyle}>
          + Add Navigation Item
        </button>
      </div>

      <CheckboxField
        label="Language Selector (TH/EN)"
        checked={showLangSelector}
        onChange={(v) => setProp((props: NavbarProps) => (props.showLangSelector = v))}
      />

      <div>
        <label style={labelStyle}>Extra Buttons</label>
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {(extraButtons || []).map((btn: ExtraButton, index: number) => (
            <div key={index} style={itemCardStyle}>
              {/* Header with reorder and delete */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "8px",
                }}
              >
                <div style={{ display: "flex", gap: "4px" }}>
                  <button
                    onClick={() => moveExtraButton(index, "up")}
                    disabled={index === 0}
                    style={{
                      ...removeButtonStyle,
                      opacity: index === 0 ? 0.3 : 1,
                      padding: "4px 8px",
                    }}
                  >
                    ↑
                  </button>
                  <button
                    onClick={() => moveExtraButton(index, "down")}
                    disabled={index === (extraButtons || []).length - 1}
                    style={{
                      ...removeButtonStyle,
                      opacity: index === (extraButtons || []).length - 1 ? 0.3 : 1,
                      padding: "4px 8px",
                    }}
                  >
                    ↓
                  </button>
                </div>
                <button onClick={() => removeExtraButton(index)} style={removeButtonStyle}>
                  ×
                </button>
              </div>
              {/* Inputs */}
              <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <input
                  type="text"
                  value={btn.text}
                  onChange={(e) => updateExtraButton(index, "text", e.target.value)}
                  style={inputStyle}
                  placeholder="Button Text"
                />
                <input
                  type="text"
                  value={btn.link}
                  onChange={(e) => updateExtraButton(index, "link", e.target.value)}
                  style={inputStyle}
                  placeholder="/link"
                />
                <select
                  value={btn.variant}
                  onChange={(e) => updateExtraButton(index, "variant", e.target.value)}
                  style={inputStyle}
                >
                  <option value="filled">Filled</option>
                  <option value="outline">Outline</option>
                  <option value="text">Text</option>
                </select>
                {btn.variant === "filled" && (
                  <input
                    type="text"
                    value={(btn as ExtraButton).bgColor || ""}
                    onChange={(e) => updateExtraButton(index, "bgColor" as keyof ExtraButton, e.target.value)}
                    style={inputStyle}
                    placeholder="BG Color (e.g. #06C755)"
                  />
                )}
                <div style={{ display: "flex", gap: "12px", marginTop: "4px" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "0.75rem", color: "#6b7280" }}>
                    <input
                      type="checkbox"
                      checked={!!btn.hideOnDesktop}
                      onChange={(e) =>
                        setProp((props: NavbarProps) => {
                          const btns = [...(props.extraButtons || [])];
                          btns[index] = { ...btns[index], hideOnDesktop: e.target.checked || undefined };
                          props.extraButtons = btns;
                        })
                      }
                    />
                    Hide Desktop
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "0.75rem", color: "#6b7280" }}>
                    <input
                      type="checkbox"
                      checked={!!btn.hideOnMobile}
                      onChange={(e) =>
                        setProp((props: NavbarProps) => {
                          const btns = [...(props.extraButtons || [])];
                          btns[index] = { ...btns[index], hideOnMobile: e.target.checked || undefined };
                          props.extraButtons = btns;
                        })
                      }
                    />
                    Hide Mobile
                  </label>
                </div>
              </div>
            </div>
          ))}
        </div>
        <button onClick={addExtraButton} style={addButtonStyle}>
          + Add Extra Button
        </button>
      </div>

      <div>
        <label style={labelStyle}>Extra Icons</label>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          💡 คลิกที่ icon บน canvas เพื่อแก้ไข รูป, link
        </Typography>
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {(extraIcons || []).map((icon: ExtraIcon, index: number) => (
            <div key={index} style={itemCardStyle}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                <Typography variant="caption" fontWeight={600} color="text.secondary">
                  Icon {index + 1}
                </Typography>
                <button
                  onClick={() =>
                    setProp((props: NavbarProps) => {
                      props.extraIcons = (props.extraIcons || []).filter((_, i) => i !== index);
                    })
                  }
                  style={removeButtonStyle}
                >
                  ×
                </button>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <ImageUpload
                  value={icon.iconUrl}
                  onChange={(url) =>
                    setProp((props: NavbarProps) => {
                      const icons = [...(props.extraIcons || [])];
                      icons[index] = { ...icons[index], iconUrl: url || "" };
                      props.extraIcons = icons;
                    })
                  }
                  label="Icon Image"
                />
                <input
                  type="text"
                  value={icon.link}
                  onChange={(e) =>
                    setProp((props: NavbarProps) => {
                      const icons = [...(props.extraIcons || [])];
                      icons[index] = { ...icons[index], link: e.target.value };
                      props.extraIcons = icons;
                    })
                  }
                  style={inputStyle}
                  placeholder="Link URL"
                />
                <input
                  type="text"
                  value={icon.bgColor || ""}
                  onChange={(e) =>
                    setProp((props: NavbarProps) => {
                      const icons = [...(props.extraIcons || [])];
                      icons[index] = { ...icons[index], bgColor: e.target.value || undefined };
                      props.extraIcons = icons;
                    })
                  }
                  style={inputStyle}
                  placeholder="BG Color (e.g. #06C755)"
                />
              </div>
            </div>
          ))}
        </div>
        <button
          onClick={() =>
            setProp((props: NavbarProps) => {
              props.extraIcons = [...(props.extraIcons || []), { iconUrl: "", link: "#" }];
            })
          }
          style={addButtonStyle}
        >
          + Add Icon
        </button>
      </div>

      <CheckboxField
        label="Show CTA Button"
        checked={showCTA}
        onChange={(v) => setProp((props: NavbarProps) => (props.showCTA = v))}
      />

      {showCTA && (
        <div style={{ ...itemCardStyle, backgroundColor: "#f0fdf4" }}>
          <span
            style={{
              fontSize: "0.875rem",
              fontWeight: 500,
              color: "#374151",
              display: "block",
              marginBottom: "12px",
            }}
          >
            CTA Button Settings
          </span>
          <SettingsRow>
            <TextField
              label="Button Link"
              value={liffUrl ?? ctaLink}
              onChange={(v) => {
                if (!liffUrl) setProp((props: NavbarProps) => (props.ctaLink = v));
              }}
              disabled={!!liffUrl}
            />
            <ColorField
              label="CTA Background"
              value={ctaBackgroundColor || ""}
              onChange={(v) => setProp((props: NavbarProps) => (props.ctaBackgroundColor = v || undefined))}
            />
          </SettingsRow>
          <SettingsRow>
            <CheckboxField
              label="Hide on Desktop"
              checked={ctaHideOnDesktop}
              onChange={(v) => setProp((props: NavbarProps) => (props.ctaHideOnDesktop = v))}
            />
            <CheckboxField
              label="Hide on Mobile"
              checked={ctaHideOnMobile}
              onChange={(v) => setProp((props: NavbarProps) => (props.ctaHideOnMobile = v))}
            />
          </SettingsRow>
        </div>
      )}

      <CheckboxField
        label="Sticky Navigation"
        checked={sticky}
        onChange={(v) => setProp((props: NavbarProps) => (props.sticky = v))}
      />

      <SliderField
        label="Max Width"
        value={maxWidth}
        onChange={(v) => setProp((props: NavbarProps) => (props.maxWidth = v))}
        min={0}
        max={1600}
        unit="px"
      />

      <SliderField
        label="Mobile Breakpoint"
        value={mobileBreakpoint}
        onChange={(v) => setProp((props: NavbarProps) => (props.mobileBreakpoint = v))}
        min={320}
        max={1024}
        step={8}
        unit="px"
      />

      <SliderField
        label="Padding X"
        value={paddingX}
        onChange={(v) => setProp((props: NavbarProps) => (props.paddingX = v))}
        min={0}
        max={120}
        unit="px"
      />
      <SettingsRow>
        <SliderField
          label="Padding Top"
          value={paddingTop}
          onChange={(v) => setProp((props: NavbarProps) => (props.paddingTop = v))}
          min={0}
          max={120}
          unit="px"
        />
        <SliderField
          label="Padding Bottom"
          value={paddingBottom}
          onChange={(v) => setProp((props: NavbarProps) => (props.paddingBottom = v))}
          min={0}
          max={120}
          unit="px"
        />
      </SettingsRow>

      <SliderField
        label="Gap"
        value={gap}
        onChange={(v) => setProp((props: NavbarProps) => (props.gap = v))}
        min={0}
        max={48}
        unit="px"
      />

      <SettingsRow>
        <ColorField
          label="Background"
          value={backgroundColor}
          onChange={(v) => setProp((props: NavbarProps) => (props.backgroundColor = v))}
        />
        <ColorField
          label="Text Color"
          value={textColor}
          onChange={(v) => setProp((props: NavbarProps) => (props.textColor = v))}
        />
      </SettingsRow>
    </SettingsSection>
  );
};

Navbar.craft = {
  displayName: "Navbar",
  isCanvas: true,
  props: {
    logoText: "Clinic",
    logoUrl: undefined,
    navItems: [],
    extraButtons: [],
    extraIcons: [],
    showCTA: false,
    ctaText: "Book Now",
    ctaLink: "/contact",
    ctaBackgroundColor: undefined,
    backgroundColor: "#ffffff",
    textColor: "#1f2937",
    sticky: true,
    maxWidth: 1280,
    paddingX: 24,
    paddingTop: 12,
    paddingBottom: 12,
    gap: 16,
    mobileBreakpoint: 768,
    showLangSelector: false,
    languages: ["TH", "EN"],
    navLayout: "right",
    navFontSize: 14,
    navFontFamily: undefined,
  },
  rules: {
    canDrag: () => true,
    canMoveIn: () => false,
    canMoveOut: () => false,
  },
  related: {
    settings: NavbarSettings,
  },
};
